// Single source of truth for how an enquiry's status is SHOWN, across every
// surface that shows one: the Report Enquiry list and its metric tiles, the
// Sent-to-CLIK slider header, the workforce detail's enquiry switcher, and the
// status chip that colours all of them.
//
// ─── WHY THIS FILE EXISTS ────────────────────────────────────────────────────
//
// The same map was copy-pasted into three pages, so every correction had to be
// made three times and one of them was always missed. The last one shipped this
// wrong: all three park reasons rendered as "Pending Payment", and only one of
// them is about money.
//
//   pending_payment    quota is empty AND the company's saldo does not cover the
//                      request. The customer can act — a top up is the whole
//                      remedy — and a clock is running: after
//                      PENDING_PAYMENT_EXPIRY_DAYS the request is cancelled.
//   parked_at_create   we could not reach the billing service, could not price
//                      the company's plan, or could not read its refusal. Ours,
//                      not theirs. No clock, never auto-cancelled, and a top up
//                      changes nothing — so "Pending Payment" here asks the
//                      customer to pay for something payment cannot release, and
//                      then leaves them wondering why nothing happened.
//   parked_at_dispatch consent was already collected; only the reservation was
//                      missing when it was due to go to the bureau. No payment
//                      clock either.
//
// A NULL reason is a row that predates the column and is `parked_at_create` by
// history — the same reading the backend sweeps give it.
//
// ─── PREFER THE BACKEND'S ANSWER ─────────────────────────────────────────────
//
// `report_enquiry.repositories.js` computes `status_label` from the same taxonomy
// and ships it on list rows, on the detail payload and on every history row. Use
// it when it is there; this map is the fallback for a payload that predates it
// (an older pod still serving during a rolling deploy) and for the switcher feed,
// which ships `status` + `quota_hold_reason` rather than a label.

export const ENQUIRY_STATUS_LABELS = {
  awaiting_form: 'Awaiting Form',
  awaiting_admin: 'Awaiting Admin Approval',
  awaiting_consent: 'Awaiting Consent',
  verification: 'Verification',
  form_revision: 'Form Revision',
  sent_to_clik: 'Sent to CLIK',
  // The BUCKET name — the list filter, the metric tile, a row whose reason we do
  // not have. True of all three parks and promising none of them anything.
  quota_hold: 'On Hold',
  completed: 'Completed',
  failed: 'Failed',
  canceled: 'Canceled',
}

// Two labels, not three: the distinction a customer can act on is "your money is
// needed" versus "it is not". The finer cause is already spelled out in words on
// the enquiry's own timeline.
export const PARK_REASON_LABELS = {
  pending_payment: 'Pending Payment',
  parked_at_create: 'On Hold',
  parked_at_dispatch: 'On Hold',
}

/**
 * The label for one enquiry-shaped object: anything carrying `status`, and
 * optionally `status_label` and `quota_hold_reason`.
 */
export function enquiryStatusLabel(row) {
  if (!row) return ''
  if (row.status_label) return row.status_label
  const status = row.status
  if (status !== 'quota_hold') return ENQUIRY_STATUS_LABELS[status] || status || ''
  return (
    PARK_REASON_LABELS[row.quota_hold_reason || 'parked_at_create']
    || ENQUIRY_STATUS_LABELS.quota_hold
  )
}
